export class SdkError extends Error {
  readonly code: string;
  readonly httpStatus: number;
  readonly retryAfter?: number;

  constructor(message: string, code: string, httpStatus: number, retryAfter?: number) {
    super(message);
    this.name = 'SdkError';
    this.code = code;
    this.httpStatus = httpStatus;
    if (retryAfter !== undefined) this.retryAfter = retryAfter;
  }
}

export class SdkAuthError extends SdkError {
  constructor(message: string, code: string) {
    super(message, code, 401);
    this.name = 'SdkAuthError';
  }
}

export class SdkRateLimitError extends SdkError {
  constructor(message: string, code: string, retryAfter: number) {
    super(message, code, 429, retryAfter);
    this.name = 'SdkRateLimitError';
  }
}

export class SdkValidationError extends SdkError {
  constructor(message: string, code: string, httpStatus: 400 | 413) {
    super(message, code, httpStatus);
    this.name = 'SdkValidationError';
  }
}

export async function mapError(res: Response): Promise<SdkError> {
  let body: any = {};
  try {
    body = await res.json();
  } catch {
    // body não-JSON; mantém defaults
  }
  const message: string = body?.error ?? `Request failed (${res.status})`;
  // Backend serializes errors as `{ error, code }` (errorHandler.ts uses `code`
  // as the field name on the wire). Older internal docs used `logCode` — kept
  // as a fallback in case any handler still emits the old shape. SDK 0.16.0
  // (W2-T7): per-app feature gates like `payments.checkout.pix_once_disabled`
  // ride on this field, so reading it accurately is required for the
  // `MethodUnavailableResult` discriminated path in `subscription.checkout`.
  const logCode: string | undefined = body?.code ?? body?.logCode;

  if (res.status === 401) {
    return new SdkAuthError(message, logCode ?? 'auth.unauthorized');
  }
  if (res.status === 429) {
    const retryAfter = parseInt(res.headers.get('Retry-After') ?? '60', 10);
    return new SdkRateLimitError(message, logCode ?? 'rate.limit_exceeded', isNaN(retryAfter) ? 60 : retryAfter);
  }
  if (res.status === 400 || res.status === 413) {
    return new SdkValidationError(message, logCode ?? `http.${res.status}`, res.status as 400 | 413);
  }
  if (res.status >= 500) {
    return new SdkError(message, logCode ?? 'http.5xx', res.status);
  }
  return new SdkError(message, logCode ?? `http.${res.status}`, res.status);
}
